import { useState, useMemo, useEffect, useLayoutEffect } from "react";
import {
  map,
  switchMap,
  filter,
  catchError,
  distinctUntilChanged
} from "rxjs/operators";
import { from, Subject, of, merge } from "rxjs";

import api from "./api";

type State<Data> = {
  loading: boolean;
  error: undefined | Error;
  data: Data;
};

export function useFetch<Result>(url: string) {
  const [result, setResult] = useState<State<Result | null>>({
    loading: false,
    error: undefined,
    data: null
  });
  const { loading, error, data } = result;
  const { reset, queryEmmit$ } = useMemo(() => {
    return {
      queryEmmit$: new Subject<string>(),
      reset: () => {
        setResult({
          loading: false,
          error: undefined,
          data: null
        });
      }
    };
  }, []);

  useLayoutEffect(() => {
    const query$ = queryEmmit$.pipe(
      filter(url => !!url),
      distinctUntilChanged((next, prev) => next === prev),
      switchMap(url =>
        merge(
          of({
            ...result,
            loading: true
          }),
          from(api.get(url)).pipe(
            map((res: any) => ({
              data: res.data as Result | null,
              loading: false,
              error: undefined
            })),
            catchError(err =>
              of({
                ...result,
                loading: false,
                error: err
              })
            )
          )
        )
      )
    );

    const subscription = query$.subscribe(value => {
      setResult(value);
    });

    return () => {
      subscription.unsubscribe();
      queryEmmit$.complete();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    queryEmmit$.next(url);
  });

  return {
    reset,
    data,
    loading,
    error
  };
}

export function useGetManual<Result>(defaultValue: any) {
  const [result, setResult] = useState<State<Result | null>>({
    loading: false,
    error: undefined,
    data: defaultValue
  });
  const { loading, error, data } = result;

  const { queryEmmit$, reset } = useMemo(() => {
    const queryEmmit$ = new Subject<{ url: string; errorMessage?: string }>();
    return {
      queryEmmit$: queryEmmit$,
      reset: () => {
        setResult({
          loading: false,
          error: undefined,
          data: defaultValue
        });
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const query$ = queryEmmit$.pipe(
      filter(({ url }) => !!url),
      switchMap(({ url, errorMessage }) =>
        merge(
          of({
            ...result,
            loading: true
          }),
          from(api.get(url)).pipe(
            map((res: any) => ({
              data: res.data as Result | null,
              loading: false,
              error: undefined
            })),
            catchError(err =>
              of({
                ...result,
                loading: false,
                error: errorMessage ? new Error(errorMessage) : err
              })
            )
          )
        )
      )
    );

    const subscription = query$.subscribe(value => {
      setResult(value);
    });

    return () => {
      subscription.unsubscribe();
      queryEmmit$.complete();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    reset,
    data,
    loading,
    error,
    query: (url: string, errorMessage?: string) => {
      queryEmmit$.next({ url, errorMessage });
    }
  };
}
